import React from 'react';
import PropTypes from 'prop-types';
// https://github.com/import-js/eslint-plugin-import/issues/2266
// eslint-disable-next-line import/no-unresolved
import { A11y, Navigation } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from '@emotion/styled';

import { outputIfNoVariables, createOptionMap } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import useUserContext from '../../hooks/use-user-context';
import useSwiperRef from '../../hooks/use-swiper-ref';
import { shadow } from '../../styles/box';
import { containerStyles } from '../../styles/layout';
import { body, headers, linkStyles } from '../../styles/type';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';

import ButtonArrow from '../buttons/ButtonArrow';
import ButtonText from '../buttons/ButtonText';
import TextRenderer from '../TextRenderer';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  box-sizing: border-box;
  position: relative;
  height: 100%;
  ${containerStyles.regular}
`;

const StyledSwiper = styled(Swiper)`
  padding: ${rem(32)} 0;

  ${media('>small')} {
    padding: ${rem(40)} 0;
  }
`;

const Slide = styled.div`
  ${shadow}
  height: 100%;
  border-radius: ${rem(10)};
  background-color: ${colors.white};
  overflow: hidden;

  ${({ arrowPadding }) => (arrowPadding && `
    padding-bottom: ${rem(108)};
  `)}

  ${media('>small')} {
    display: flex;
    flex-direction: row;
  }
`;

const ImageContainer = styled.div`
  max-width: 75%;
  margin-bottom: ${rem(16)};

  ${media('>small')} {
    flex: 0 0 ${rem(400)};
    height: auto;
  }
`;

const ImageMask = styled.div`
  position: relative;
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${colors.green['500']};
    clip-path: circle(70% at 33% 26%);
  }
`;

const Image = styled(GatsbyImage)`
  width: 100%;
  aspect-ratio: 10 / 9;
  clip-path: circle(65% at 33% 26%);
`;

const TextContent = styled.div`
  padding: ${rem(20)};

  ${media('>tiny')} {
    padding: ${rem(40)};
  }

  ${media('>small')} {
    flex: 0 0 68%;
  }
`;

const Subhead = styled.h4`
  ${headers.eyebrow}
  color: ${colors.gray['600']};
  margin-bottom: ${rem(12)};
`;

const Headline = styled.h3`
  ${headers.h3}
  color: ${colors.blue['800']};
`;

const Text = styled.div`
  ${body.regular}
  ${linkStyles}
  color: ${colors.gray['600']};
  margin: ${rem(16)} 0 ${rem(36)} 0;
`;

const ArrowsContainer = styled.div`
  ${containerStyles.regular}
  position: relative;
`;

const SliderArrows = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 0;
  display: flex;
  padding-bottom: ${rem(32)};
  padding-left: ${rem(52)};

  ${media('>smedium')} {
    padding-left: ${rem(72)};
  }

  ${ButtonArrow} + ${ButtonArrow} {
    margin-left: ${rem(16)};
  }
`;

const FeaturedContent = ({ featuredContentItems, backgroundColor }) => {
  const data = useStaticQuery(graphql`
    query FeaturedContent {
      allContentfulOptionSet(filter: {name: {eq: "slider"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);

  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const [prevButton, prevButtonRef] = useSwiperRef();
  const [nextButton, nextButtonRef] = useSwiperRef();

  return (
    <Container
      backgroundColor={backgroundColor}
    >
      <StyledSwiper
        slidesPerView={1}
        modules={[Navigation, A11y]}
        navigation={{
          prevEl: prevButton,
          nextEl: nextButton
        }}
        loop={featuredContentItems.length > 1}
      >
        {featuredContentItems.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <SwiperSlide key={index} style={{ height: 'auto' }}>
            <ContentWrapper>
              <Slide arrowPadding={featuredContentItems.length > 1}>
                <ImageContainer>
                  <ImageMask>
                    <Image image={getImage(item.image)} alt={item.image.description} />
                  </ImageMask>
                </ImageContainer>
                <TextContent>
                  {item.subhead && (
                    <Subhead>
                      <TextRenderer text={item.subhead} />
                    </Subhead>
                  )}
                  {item.headline && (
                    <Headline>
                      <TextRenderer text={item.headline} />
                    </Headline>
                  )}
                  <Text>
                    <TextRenderer text={item.text} richText />
                  </Text>
                  {item.ctaText && item.ctaLink && (
                    <ButtonText
                      to={item.ctaLink}
                      aria-label={outputIfNoVariables(`${item.headline} — ${item.ctaText}`)}
                    >
                      <TextRenderer text={item.ctaText} />
                    </ButtonText>
                  )}
                </TextContent>
              </Slide>
            </ContentWrapper>
          </SwiperSlide>
        ))}
        {featuredContentItems.length > 1 && (
          <ArrowsContainer>
            <SliderArrows>
              <ButtonArrow
                left
                ref={prevButtonRef}
                aria-label={options['slider.prev_button_label']}
              />
              <ButtonArrow
                ref={nextButtonRef}
                aria-label={options['slider.next_button_label']}
              />
            </SliderArrows>
          </ArrowsContainer>
        )}
      </StyledSwiper>
    </Container>
  );
};

FeaturedContent.propTypes = {
  featuredContentItems: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.shape({
        // eslint-disable-next-line react/forbid-prop-types
        gatsbyImageData: PropTypes.object,
        description: PropTypes.string
      }),
      subhead: PropTypes.string,
      headline: PropTypes.string,
      text: PropTypes.shape({
        raw: PropTypes.string
      }),
      ctaText: PropTypes.string,
      ctaLink: PropTypes.string
    })
  ),
  backgroundColor: PropTypes.oneOf(['Blue', 'White'])
};

export const query = graphql`
  fragment FeaturedContentFragment on ContentfulFeaturedContent {
    id
    __typename
    featuredContentItems {
      id
      image {
        gatsbyImageData(width: 400, height: 360, quality: 75, resizingBehavior: FILL)
        description
      }
      subhead
      headline
      text {
        raw
      }
      ctaText
      ctaLink
    }
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default FeaturedContent;
